import { getAddress } from "viem";
import { SYNDICATE_FACTORY_ABI, SYNDICATE_VAULT_ABI } from "../abis.js";
import { getDeployment } from "../addresses.js";
import { notFound, unavailable, usage } from "../errors.js";
const SECONDS_PER_DAY = 86400n;
export async function readActiveFunds(client, chainId, opts = {}) {
    const factory = getDeployment(chainId).sherwood.factory;
    let raw;
    try {
        raw = (await client.readContract({
            address: factory,
            abi: SYNDICATE_FACTORY_ABI,
            functionName: "getAllActiveSyndicates",
        }));
    }
    catch (err) {
        throw unavailable(`Failed to read getAllActiveSyndicates from chain ${chainId}`, err);
    }
    // Newest-first, then truncate to `limit`. Default 25, hard cap 100 — same
    // shape as /v1/proposals.
    const limit = Math.min(opts.limit ?? 25, 100);
    const sorted = [...raw].sort((a, b) => a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0);
    const page = sorted.slice(0, limit);
    if (page.length === 0)
        return [];
    // One round-trip per vault: totalAssets + getAgentCount + paused +
    // openDeposits. Use Promise.all rather than multicall3 because the SDK
    // doesn't ship the multicall3 ABI today (would add weight + a new dep).
    // Page is small (default 25) so the parallel-RPC fan-out is fine.
    const enriched = await Promise.all(page.map(async (s) => {
        const [totalAssets, agentCount, paused, openDeposits] = await Promise.all([
            client
                .readContract({
                address: s.vault,
                abi: SYNDICATE_VAULT_ABI,
                functionName: "totalAssets",
            })
                .catch(() => 0n),
            client
                .readContract({
                address: s.vault,
                abi: SYNDICATE_VAULT_ABI,
                functionName: "getAgentCount",
            })
                .catch(() => 0n),
            client
                .readContract({
                address: s.vault,
                abi: SYNDICATE_VAULT_ABI,
                functionName: "paused",
            })
                .catch(() => false),
            client
                .readContract({
                address: s.vault,
                abi: SYNDICATE_VAULT_ABI,
                functionName: "openDeposits",
            })
                .catch(() => false),
        ]);
        const nowSec = BigInt(Math.floor(Date.now() / 1000));
        const ageSec = nowSec > s.createdAt ? nowSec - s.createdAt : 0n;
        return {
            id: s.id.toString(),
            vault: s.vault,
            creator: s.creator,
            subdomain: s.subdomain,
            metadataURI: s.metadataURI,
            createdAt: s.createdAt.toString(),
            ageDays: Number(ageSec / SECONDS_PER_DAY),
            agentCount: Number(agentCount),
            totalAssets: totalAssets.toString(),
            paused,
            openDeposits,
        };
    }));
    return enriched;
}
/**
 * Resolve a fund's id (and canonical vault/creator/subdomain) from either
 * its vault address or its subdomain — the reliable path the issue's reporter
 * lacked. `vault.syndicateId()` does not exist; resolution goes through the
 * factory's `vaultToSyndicate` / `subdomainToSyndicate` → `syndicates(id)`.
 *
 * Throws USAGE when neither key is given, NOT_FOUND when nothing maps.
 */
export async function readFundResolution(client, chainId, by) {
    const factory = getDeployment(chainId).sherwood.factory;
    let id;
    if (by.vault) {
        id = (await client.readContract({
            address: factory,
            abi: SYNDICATE_FACTORY_ABI,
            functionName: "vaultToSyndicate",
            args: [getAddress(by.vault)],
        }));
    }
    else if (by.subdomain) {
        id = (await client.readContract({
            address: factory,
            abi: SYNDICATE_FACTORY_ABI,
            functionName: "subdomainToSyndicate",
            args: [by.subdomain],
        }));
    }
    else {
        throw usage("Provide `vault` or `subdomain` to resolve a fund");
    }
    if (id === 0n) {
        throw notFound(`No fund found for ${by.vault ? `vault ${by.vault}` : `subdomain ${by.subdomain}`} on chain ${chainId}`);
    }
    const s = (await client.readContract({
        address: factory,
        abi: SYNDICATE_FACTORY_ABI,
        functionName: "syndicates",
        args: [id],
    }));
    return {
        fundId: s[0].toString(),
        vault: s[1],
        creator: s[2],
        subdomain: s[6],
    };
}
